import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { AlertCircle, CheckCircle2, X } from "lucide-react";

type ToastTone = "success" | "error";

interface ToastItem {
  id: string;
  tone: ToastTone;
  message: string;
}

interface ShowToastOptions {
  /** A stable id replaces the existing toast in place instead of stacking. */
  id?: string;
  duration?: number;
}

interface ToastContextValue {
  showToast: (tone: ToastTone, message: string, options?: ShowToastOptions) => void;
}

const ToastContext = createContext<ToastContextValue | null>(null);

const DEFAULT_DURATION = 4000;
let autoIdCounter = 0;

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<ToastItem[]>([]);
  const timers = useRef(new Map<string, ReturnType<typeof setTimeout>>());

  const dismiss = useCallback((id: string) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
    const timer = timers.current.get(id);
    if (timer) {
      clearTimeout(timer);
      timers.current.delete(id);
    }
  }, []);

  const showToast = useCallback(
    (tone: ToastTone, message: string, options?: ShowToastOptions) => {
      const id = options?.id ?? `toast-${++autoIdCounter}`;
      const duration = options?.duration ?? DEFAULT_DURATION;

      setToasts((prev) => {
        const index = prev.findIndex((t) => t.id === id);
        if (index === -1) return [...prev, { id, tone, message }];
        // Replace in place: several parallel requests failing at once would
        // otherwise stack identical toasts down the whole screen.
        const next = [...prev];
        next[index] = { id, tone, message };
        return next;
      });

      // Restart the clock, so a replaced toast gets its full duration rather
      // than inheriting whatever was left of the previous one.
      const existing = timers.current.get(id);
      if (existing) clearTimeout(existing);
      timers.current.set(
        id,
        setTimeout(() => dismiss(id), duration),
      );
    },
    [dismiss],
  );

  const value = useMemo(() => ({ showToast }), [showToast]);

  return (
    <ToastContext.Provider value={value}>
      {children}
      <div
        className="pointer-events-none fixed inset-x-4 bottom-4 z-[100] flex flex-col items-stretch gap-2 sm:inset-x-auto sm:bottom-6 sm:start-6 sm:items-start"
        aria-live="polite"
        role="status"
      >
        {toasts.map((toast) => (
          <div
            key={toast.id}
            // Always white: the tone is carried by the border and the icon only.
            // A fully coloured toast competes with the interface behind it.
            className={`pointer-events-auto flex w-full animate-slide-in-end items-start gap-2 rounded-lg border bg-card p-3 text-sm text-ink-800 shadow-md sm:w-72 ${
              toast.tone === "success" ? "border-success-border" : "border-danger-border"
            }`}
          >
            {toast.tone === "success" ? (
              <CheckCircle2 className="mt-0.5 size-4 shrink-0 text-success" aria-hidden="true" />
            ) : (
              <AlertCircle className="mt-0.5 size-4 shrink-0 text-danger" aria-hidden="true" />
            )}
            <span className="flex-1">{toast.message}</span>
            <button
              type="button"
              onClick={() => dismiss(toast.id)}
              aria-label="Dismiss notification"
              className="text-muted-subtle transition-colors hover:text-ink-700"
            >
              <X className="size-3.5" />
            </button>
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
}

export function useToast(): ToastContextValue {
  const context = useContext(ToastContext);
  if (!context) throw new Error("useToast must be used inside a ToastProvider");
  return context;
}
